import i18nPackage from "../../i18n/i18n";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import styles from "./index.module.css";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import { useEffect, useMemo, useState } from "react";
import { sortPresetProfiles } from "../profile/profileHelpers";
import ProfileList, { PresetProfileGrid, ProfileListProps } from "../profile/ProfileList";
import { CoreSkeletonWrapper } from "../../core";
import { isEnforcedProfile } from "@verity/proctoring-option";
import { EditQuizPageProps } from "../editQuiz";

type ProfileStepProps = {
	activeProfile: ProctoringOptionProfileV2 | undefined;
	isCustomConfig: boolean;
	onProfileSelect: (profileId: number | undefined) => void;
	loading?: boolean;
	showPresetProfile: boolean;
} & Pick<
	ProfileListProps,
	| "canDeleteProfile"
	| "onDelete"
	| "proctoringData"
	| "verityInstituteSettings"
	| "optionRelatedInstituteSettings"
	| "enforcedProfilesOnly"
	| "profiles"
	| "setupGuideState"
	| "lms"
> &
	Pick<EditQuizPageProps, "currentProfileId">;

export default function ProfileStep({
	profiles,
	activeProfile,
	isCustomConfig,
	onProfileSelect,
	loading,
	enforcedProfilesOnly,
	verityInstituteSettings,
	optionRelatedInstituteSettings,
	proctoringData,
	showPresetProfile,
	canDeleteProfile,
	onDelete,
	setupGuideState,
	currentProfileId,
	lms,
}: ProfileStepProps) {
	const [isViewingProfile, setIsViewingProfile] = useState(false);
	const presetProfiles = useMemo(() => sortPresetProfiles(profiles), [profiles]);

	// If first time creating a quiz, auto select either the first preset or the first profile
	useEffect(() => {
		// currentProfileId alone can't tell "custom explicitly chosen" (id undefined) apart from "nothing
		// chosen yet" (also id undefined) — isCustomConfig is what carries that distinction. currentProfileId
		// still matters on its own for a seeded id that never resolves to a fetched profile (e.g. Lumina's
		// synthetic "assessment" profile): there's already a decision, just not one this list can show.
		// Tested against undefined, not truthiness, to match loadProfile: an id of 0
		// is a real decision and must not read as "nothing chosen yet" here either.
		if (currentProfileId !== undefined || isCustomConfig) {
			const isEnforcedAndInvalid = enforcedProfilesOnly && activeProfile && !isEnforcedProfile(activeProfile);

			//  return early if theres an active profile and there is no issues with enforce profiles
			if (activeProfile && !isEnforcedAndInvalid) {
				return;
			}

			// If custom profile is selected
			if (!enforcedProfilesOnly) {
				return;
			}
		}

		// Select the first preset
		if (showPresetProfile && presetProfiles.length !== 0) {
			onProfileSelect(presetProfiles[0].id);
		}

		// If there are profiles select the first one
		else if (!showPresetProfile && profiles.length > 0) {
			onProfileSelect(profiles[0].id);
		}
	}, []);

	return (
		<StepWrapper>
			<div className={styles.profileStep}>
				{!isViewingProfile && (
					<StepDescription loading={loading}>
						{enforcedProfilesOnly
							? i18nPackage.t("editQuizPage.profileStep.institutionRequiredProfiles")
							: i18nPackage.t("editQuizPage.profileStep.description")}
					</StepDescription>
				)}
				<CoreSkeletonWrapper loading={loading} height={"100%"}>
					{showPresetProfile && !enforcedProfilesOnly && presetProfiles.length > 0 ? (
						<PresetProfileGrid
							liteMode={verityInstituteSettings.liteMode}
							presetProfiles={presetProfiles}
							onSelect={onProfileSelect}
							selectedId={activeProfile?.id}
						/>
					) : (
						<ProfileList
							enforcedProfilesOnly={enforcedProfilesOnly}
							optionRelatedInstituteSettings={optionRelatedInstituteSettings}
							proctoringData={proctoringData}
							profiles={profiles}
							selectedId={activeProfile?.id}
							onProfileSelected={onProfileSelect}
							verityInstituteSettings={verityInstituteSettings}
							showCustomButton={true}
							canDeleteProfile={canDeleteProfile}
							onDelete={onDelete}
							onViewProfile={(profile) => setIsViewingProfile(Boolean(profile))}
							setupGuideState={setupGuideState}
							lms={lms}
						/>
					)}
				</CoreSkeletonWrapper>
			</div>
		</StepWrapper>
	);
}
